import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router";

// ─── Colour palette ───────────────────────────────────────────────────────────
const RANK_COLORS = ["#FFD700", "#5B8DEF", "#EC5555", "#4CAF50", "#FF9800"];
const BAR_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const RANK_STROKES = ["#8a7100", "#505050", "#7a4c1e", "#222222", "#7a1010"];

// ─── Hardcoded analysis data ──────────────────────────────────────────────────

const STATS = {
  tsarTurns: 112407,
  binaryComparisons: 1124070,
  games: 15833,
  playerSessions: 31454,
};

interface CardStat {
  answer: string;
  elo: number;
  dist: number[];
  entropy: number;
  wins: number;
  losses: number;
}

const LOVE_LANGUAGES: CardStat[] = [
  { answer: "Quality Time", elo: 1643, dist: [313, 279, 184, 76, 30], entropy: 0.86, wins: 2533, losses: 995 },
  { answer: "Physical Touch", elo: 1621, dist: [290, 200, 157, 140, 95], entropy: 0.96, wins: 2214, losses: 1314 },
  { answer: "Words of Affirmation", elo: 1483, dist: [92, 158, 223, 228, 181], entropy: 0.97, wins: 1516, losses: 2012 },
  { answer: "Acts of Service", elo: 1475, dist: [131, 167, 207, 243, 134], entropy: 0.98, wins: 1682, losses: 1846 },
  { answer: "Gift Giving", elo: 1287, dist: [56, 78, 111, 195, 442], entropy: 0.83, wins: 875, losses: 2653 },
];

const ANGRY_FRIEND_TOP: CardStat[] = [
  { answer: "They slept with your partner", elo: 1997, dist: [131, 31, 7, 2, 4], entropy: 0.49, wins: 0, losses: 0 },
  { answer: "They burned your house down", elo: 1870, dist: [102, 41, 10, 2, 6], entropy: 0.61, wins: 0, losses: 0 },
  { answer: "They leaked an intimate secret about you", elo: 1733, dist: [80, 53, 28, 6, 2], entropy: 0.74, wins: 0, losses: 0 },
  { answer: "They cancelled a planned vacation last-minute", elo: 1728, dist: [65, 59, 36, 14, 8], entropy: 0.86, wins: 0, losses: 0 },
  { answer: "They snitched on you to the police", elo: 1658, dist: [51, 47, 37, 17, 5], entropy: 0.88, wins: 0, losses: 0 },
];

const ANGRY_FRIEND_BOTTOM: CardStat[] = [
  { answer: "They removed you on Facebook", elo: 1341, dist: [12, 26, 40, 54, 54], entropy: 0.93, wins: 0, losses: 0 },
  { answer: "They said you can't draw well", elo: 1309, dist: [13, 15, 29, 45, 68], entropy: 0.89, wins: 0, losses: 0 },
  { answer: "They asked why you're in a bad mood", elo: 1288, dist: [9, 27, 32, 65, 51], entropy: 0.91, wins: 0, losses: 0 },
  { answer: "They like pineapple on pizza", elo: 1237, dist: [10, 13, 21, 41, 98], entropy: 0.79, wins: 0, losses: 0 },
  { answer: "They said you're not a good rapper", elo: 1221, dist: [7, 8, 23, 36, 81], entropy: 0.78, wins: 0, losses: 0 },
];

const WOKEN_UP: CardStat[] = [
  { answer: "Just let them sleep", elo: 1802, dist: [41, 10, 2, 2, 3], entropy: 0.58, wins: 0, losses: 0 },
  { answer: "A gentle pat + \"good morning\"", elo: 1643, dist: [13, 32, 4, 4, 5], entropy: 0.77, wins: 0, losses: 0 },
  { answer: "A loud noise", elo: 1532, dist: [1, 8, 38, 8, 3], entropy: 0.65, wins: 0, losses: 0 },
  { answer: "A hard slap", elo: 1298, dist: [1, 3, 12, 29, 13], entropy: 0.76, wins: 0, losses: 0 },
  { answer: "Ice water poured on them", elo: 1226, dist: [2, 5, 2, 15, 34], entropy: 0.69, wins: 0, losses: 0 },
];

const MOVIE_TROPES: CardStat[] = [
  { answer: "Fake-dating that turns real", elo: 1516, dist: [15, 12, 11, 9, 10], entropy: 0.99, wins: 0, losses: 0 },
  { answer: "Sneaking away from a fancy event", elo: 1509, dist: [13, 9, 16, 10, 9], entropy: 0.98, wins: 0, losses: 0 },
  { answer: "\"There's only one bed\"", elo: 1507, dist: [6, 17, 10, 10, 14], entropy: 0.97, wins: 0, losses: 0 },
  { answer: "An argument becomes a makeout", elo: 1487, dist: [19, 6, 15, 8, 9], entropy: 0.95, wins: 0, losses: 0 },
  { answer: "Kissing to hide from the bad guys", elo: 1486, dist: [4, 13, 5, 20, 15], entropy: 0.90, wins: 0, losses: 0 },
];

const RELATIONSHIP_IMPORTANCE: CardStat[] = [
  { answer: "Loyalty", elo: 1815, dist: [152, 77, 26, 19, 4], entropy: 0.72, wins: 0, losses: 0 },
  { answer: "Honesty", elo: 1749, dist: [119, 84, 47, 18, 4], entropy: 0.79, wins: 0, losses: 0 },
  { answer: "Effort", elo: 1687, dist: [89, 82, 71, 23, 12], entropy: 0.88, wins: 0, losses: 0 },
  { answer: "Open communication", elo: 1660, dist: [100, 88, 57, 32, 16], entropy: 0.90, wins: 0, losses: 0 },
  { answer: "Absolute trust", elo: 1646, dist: [114, 79, 52, 28, 16], entropy: 0.88, wins: 0, losses: 0 },
  { answer: "Respect", elo: 1642, dist: [88, 92, 58, 23, 13], entropy: 0.88, wins: 0, losses: 0 },
  { answer: "Compassion", elo: 1622, dist: [53, 86, 73, 53, 16], entropy: 0.94, wins: 0, losses: 0 },
  { answer: "Realistic expectations", elo: 1532, dist: [31, 49, 101, 68, 36], entropy: 0.94, wins: 0, losses: 0 },
  { answer: "Patience", elo: 1525, dist: [26, 84, 66, 56, 22], entropy: 0.93, wins: 0, losses: 0 },
  { answer: "Both people happy on their own", elo: 1467, dist: [44, 51, 44, 83, 48], entropy: 0.98, wins: 0, losses: 0 },
  { answer: "Shared goals", elo: 1412, dist: [29, 41, 81, 82, 47], entropy: 0.95, wins: 0, losses: 0 },
  { answer: "The sex", elo: 1392, dist: [17, 33, 64, 86, 55], entropy: 0.93, wins: 0, losses: 0 },
  { answer: "Agreeing on politics", elo: 1391, dist: [5, 22, 46, 61, 110], entropy: 0.82, wins: 0, losses: 0 },
  { answer: "Sharing a faith", elo: 1309, dist: [22, 12, 27, 59, 155], entropy: 0.76, wins: 0, losses: 0 },
  { answer: "Stability & routine", elo: 1301, dist: [12, 37, 65, 99, 64], entropy: 0.90, wins: 0, losses: 0 },
  { answer: "Money", elo: 1207, dist: [6, 9, 33, 91, 148], entropy: 0.71, wins: 0, losses: 0 },
  { answer: "Butt stuff", elo: 1187, dist: [24, 5, 20, 50, 165], entropy: 0.68, wins: 0, losses: 0 },
];

interface Correlation {
  card_a: string;
  question_a: string;
  card_b: string;
  question_b: string;
  corr: number;
  n: number;
  direction: "with" | "against";
  story: string;
}

const CORRELATIONS: Correlation[] = [
  {
    card_a: "Quality Time",
    question_a: "your love language",
    card_b: "Cook together",
    question_b: "a first date",
    corr: 0.40,
    n: 50,
    direction: "with",
    story: "People whose love language is Quality Time would rather cook together on a first date than do almost anything else.",
  },
  {
    card_a: "Quality Time",
    question_a: "your love language",
    card_b: "Go to a club",
    question_b: "a first date",
    corr: -0.41,
    n: 44,
    direction: "against",
    story: "And the same people run screaming from the idea of a club. Quality Time, it turns out, does not mean a crowded dancefloor.",
  },
  {
    card_a: "Shared goals",
    question_a: "what matters in a relationship",
    card_b: "Words of Affirmation",
    question_b: "your love language",
    corr: 0.35,
    n: 71,
    direction: "with",
    story: "People who rank Shared Goals highly in a relationship tend to have Words of Affirmation as their love language. Ambition, apparently, likes to be told out loud.",
  },
  {
    card_a: "Gift Giving",
    question_a: "your love language",
    card_b: "Eyebrows",
    question_b: "favourite body part on a partner",
    corr: 0.35,
    n: 72,
    direction: "with",
    story: "And our personal favourite, which we offer with no explanation whatsoever: people whose love language is Gift Giving disproportionately say their partner's best feature is their eyebrows.",
  },
];

// ─── Reusable viz components ──────────────────────────────────────────────────

function RankDistBar({ dist, width = 120, height = 16 }: { dist: number[]; width?: number; height?: number }) {
  const total = dist.reduce((a, b) => a + b, 0) || 1;
  const pcts = dist.map((v, i) => ({ pct: (v / total) * 100, label: `#${i + 1}: ${Math.round((v / total) * 100)}%` }));
  return (
    <Tooltip title={pcts.map((p) => p.label).join("  ")} arrow placement="top">
      <Box sx={{ display: "flex", width, height, borderRadius: 1, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", flexShrink: 0 }}>
        {pcts.map((p, i) => (
          <Box key={i} sx={{ height: "100%", width: `${p.pct}%`, bgcolor: RANK_COLORS[i], minWidth: p.pct > 0 ? 2 : 0 }} />
        ))}
      </Box>
    </Tooltip>
  );
}

function EloBar({ elo }: { elo: number }) {
  const pct = Math.max(0, Math.min(100, ((elo - 1000) / 1000) * 100));
  return (
    <Box sx={{ width: 56, height: 6, bgcolor: "rgba(0,0,0,0.06)", borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
      <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "#ec7777", borderRadius: 1 }} />
    </Box>
  );
}

function CardRow({ card, rank, totalMatches }: { card: CardStat; rank: number; totalMatches?: number }) {
  const winRate = totalMatches ? (card.wins / totalMatches * 100).toFixed(0) : null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, px: 1, borderRadius: 1.5,
      "&:hover": { bgcolor: "rgba(0,0,0,0.02)" }, transition: "background 0.15s" }}>
      <Typography sx={{ fontFamily: '"Bangers", cursive', fontSize: "1.1rem", color: RANK_COLORS[rank], minWidth: 20, textAlign: "center",
        WebkitTextStroke: `1px ${RANK_STROKES[rank]}` }}>
        {rank + 1}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{card.answer}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <EloBar elo={card.elo} />
        <Typography variant="caption" sx={{ minWidth: 38, textAlign: "right", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
          {Math.round(card.elo)}
        </Typography>
      </Box>
      <RankDistBar dist={card.dist} width={80} height={10} />
      {winRate !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: "right", display: { xs: "none", sm: "block" } }}>
          {winRate}%
        </Typography>
      )}
    </Box>
  );
}

// Big featured number — the centerpiece of a "key stat" moment
function BigNumber({ value, before, after }: { value: string; before?: string; after?: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setShown(true); }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{ textAlign: "center", py: { xs: 3, sm: 4 } }}>
      {before && (
        <Typography color="text.secondary" sx={{ mb: 0.5, fontSize: "1rem" }}>{before}</Typography>
      )}
      <Typography sx={{
        fontFamily: '"Bangers", cursive', color: "primary.main", lineHeight: 1,
        fontSize: { xs: "3.4rem", sm: "5rem" }, letterSpacing: "0.02em",
        transform: shown ? "scale(1)" : "scale(0.85)", opacity: shown ? 1 : 0,
        transition: "transform 0.5s cubic-bezier(0.34,1.4,0.64,1), opacity 0.5s",
      }}>
        {value}
      </Typography>
      {after && (
        <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: "1rem", maxWidth: 520, mx: "auto" }}>{after}</Typography>
      )}
    </Box>
  );
}

// Article-style heading
function H2({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h4" fontWeight="bold" sx={{ mt: { xs: 2, sm: 3 }, mb: 2, lineHeight: 1.2, fontSize: { xs: "1.6rem", sm: "2rem" } }}>
      {children}
    </Typography>
  );
}

// Article body paragraph
function P({ children, lead = false }: { children: React.ReactNode; lead?: boolean }) {
  return (
    <Typography sx={{
      color: lead ? "text.primary" : "rgba(0,0,0,0.72)",
      fontSize: lead ? { xs: "1.12rem", sm: "1.25rem" } : { xs: "1rem", sm: "1.08rem" },
      lineHeight: 1.75, mb: 2.5, fontWeight: lead ? 500 : 400,
    }}>
      {children}
    </Typography>
  );
}

function Hl({ children, color = "primary.main" }: { children: React.ReactNode; color?: string }) {
  return <Box component="span" sx={{ fontWeight: 700, color }}>{children}</Box>;
}

// Horizontal scroll card tray
function CardTray({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1.5,
      "&::-webkit-scrollbar": { height: 5 },
      "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.15)", borderRadius: 2 } }}>
      {children}
    </Box>
  );
}

function QuestionCard({ question, cards, predScore, n, tag, tagColor }: {
  question: string; cards: CardStat[]; predScore?: number; n?: number; tag?: string; tagColor?: string;
}) {
  const top5 = cards.slice(0, 5);
  return (
    <Paper sx={{ p: 2, borderRadius: 2, minWidth: 280, maxWidth: 340, flexShrink: 0 }}>
      {tag && (
        <Chip label={tag} size="small"
          sx={{ mb: 1, bgcolor: tagColor ?? "#eee", color: tagColor ? "#fff" : "text.primary", fontWeight: 700, fontSize: "0.65rem" }} />
      )}
      <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1.5, fontStyle: "italic", fontWeight: 500 }}>
        "{question}"
      </Typography>
      <Stack spacing={0.25}>
        {top5.map((c, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontFamily: '"Bangers", cursive', fontSize: "0.9rem", color: RANK_COLORS[i], minWidth: 16, textAlign: "center" }}>
              {i + 1}
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 500, fontSize: "0.73rem" }}>{c.answer}</Typography>
            <RankDistBar dist={c.dist} width={60} height={8} />
          </Box>
        ))}
      </Stack>
      {predScore !== undefined && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 1, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">Friends guess it right:</Typography>
          <Typography variant="caption" fontWeight="bold">{Math.round(predScore)}</Typography>
          <Typography variant="caption" color="text.secondary">/ 2000</Typography>
        </Box>
      )}
    </Paper>
  );
}

// Binary comparison illustration
function BinaryComparison({ winner, loser }: { winner: string; loser: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
      <Paper sx={{ px: 2, py: 1, borderRadius: 2, border: "2px solid #4CAF50", bgcolor: "rgba(76,175,80,0.06)" }}>
        <Typography variant="body2" fontWeight="bold">{winner}</Typography>
      </Paper>
      <Typography sx={{ color: "#4CAF50", fontWeight: 900, fontSize: "0.78rem" }}>beats</Typography>
      <Paper sx={{ px: 2, py: 1, borderRadius: 2, border: "2px solid #EC5555", bgcolor: "rgba(236,85,85,0.06)" }}>
        <Typography variant="body2" fontWeight="bold">{loser}</Typography>
      </Paper>
    </Box>
  );
}

// Static colored ranking list
function MiniRankList({ items }: { items: string[] }) {
  return (
    <Stack spacing={0.5}>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6, borderRadius: 1.5,
          bgcolor: BAR_BG[i % 5], boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Typography sx={{ fontFamily: '"Bangers", cursive', fontSize: "0.9rem", color: RANK_COLORS[i],
            WebkitTextStroke: `1px ${RANK_STROKES[i]}`, minWidth: 14, textAlign: "center" }}>
            {i + 1}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: (["#333", "#fff", "#fff", "#fff", "#fff"])[i], lineHeight: 1.2 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function SequenceConnector({ leftOrder, rightOrder }: { leftOrder: string[]; rightOrder: string[] }) {
  const barH = 30, gap = 4, stride = barH + gap;
  const seqH = 5 * stride - gap;
  const ly = (label: string) => { const i = leftOrder.indexOf(label); return i >= 0 ? ((i * stride + barH / 2) / seqH) * 100 : 50; };
  const ry = (label: string) => { const i = rightOrder.indexOf(label); return i >= 0 ? ((i * stride + barH / 2) / seqH) * 100 : 50; };
  const allItems = leftOrder.slice(0, 5);
  return (
    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: "block" }}>
      {allItems.map((item, idx) => {
        const l = ly(item), r = ry(item);
        const same = leftOrder.indexOf(item) === rightOrder.indexOf(item);
        return (
          <path key={idx} d={`M 0 ${l} C 50 ${l} 50 ${r} 100 ${r}`} fill="none"
            stroke={same ? "#A7C577" : BAR_BG[idx % 5]} strokeWidth={same ? 2.5 : 1.8} strokeOpacity={same ? 0.95 : 0.5} />
        );
      })}
    </svg>
  );
}

function CorrelationCard({ corr }: { corr: Correlation }) {
  const color = corr.direction === "with" ? "#4CAF50" : "#EC5555";
  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
        <Chip size="small" label={corr.card_a} sx={{ bgcolor: "rgba(0,0,0,0.06)", fontWeight: 600, fontSize: "0.72rem" }} />
        <Typography variant="caption" color="text.secondary">({corr.question_a})</Typography>
        <Typography sx={{ color, fontWeight: 800, fontSize: "1.1rem", px: 0.5 }}>
          {corr.direction === "with" ? "→" : "↮"}
        </Typography>
        <Chip size="small" label={corr.card_b} sx={{ bgcolor: "rgba(0,0,0,0.06)", fontWeight: 600, fontSize: "0.72rem" }} />
        <Typography variant="caption" color="text.secondary">({corr.question_b})</Typography>
      </Stack>
      <Typography sx={{ color: "rgba(0,0,0,0.72)", fontSize: "1rem", lineHeight: 1.6, mb: 1 }}>{corr.story}</Typography>
      <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
        correlation {corr.corr > 0 ? "+" : ""}{corr.corr.toFixed(2)} · {corr.n} shared players
      </Typography>
    </Paper>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AnalyzingPage() {
  const history = useHistory();

  const czarOrder = ["Quality Time", "Physical Touch", "Words of Affirmation", "Acts of Service", "Gift Giving"];
  const yourOrder = ["Physical Touch", "Quality Time", "Gift Giving", "Acts of Service", "Words of Affirmation"];

  return (
    <Box sx={{ minHeight: "100vh", py: 3 }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>

        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/")}
          sx={{ color: "text.primary", mb: 2 }}>
          Back
        </Button>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
            color: "text.disabled", display: "block", mb: 2 }}>
            Data deep-dive · June 2026
          </Typography>
          <Typography variant="h2" fontWeight="bold" sx={{ mb: 2.5, lineHeight: 1.1, fontSize: { xs: "2.3rem", sm: "3.4rem" } }}>
            Analyzing{" "}
            <Box component="span" sx={{ color: "primary.main" }}>1.1 million</Box>{" "}
            would-you-rather questions
          </Typography>
          <Typography sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, color: "text.secondary", lineHeight: 1.6, fontWeight: 400 }}>
            For two years, people have been telling our little party game what they honestly think —
            about love, friendship, betrayal, and the best way to wake someone up. Here's what a
            million tiny opinions add up to.
          </Typography>
        </Box>

        {/* ── 1. The game ──────────────────────────────────────────────── */}
        <H2>First, the game</H2>
        <P lead>
          rank.io is a party game you play with friends, and the whole thing fits in one sentence:
          one person answers a question honestly, and everyone else tries to guess how they answered.
        </P>
        <P>
          Say it's your turn. The game asks you something personal — <em>"What's your love language?"</em> —
          and hands you five cards. You drag them into your real, honest order, top to bottom:
        </P>

        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <Box sx={{ width: { xs: "100%", sm: 320 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 1, fontStyle: "italic" }}>
              "What's <strong>your</strong> love language?"
            </Typography>
            <MiniRankList items={["Quality Time", "Physical Touch", "Acts of Service", "Words of Affirmation", "Gift Giving"]} />
          </Box>
        </Box>

        <P>
          Then everyone else at the table tries to predict the exact order <em>you</em> picked. The closer
          they get, the more points they score. That's it — no trivia, no right answers, just a measure of
          how well the people in the room actually know each other.
        </P>
        <P>
          The important part, for our purposes, is that <Hl>your answer is real</Hl>. Almost everyone plays
          with their actual friends, so almost every ranking we've ever recorded is somebody's genuine
          opinion. Stack up enough of them and you get something rare: a huge, honest survey nobody
          realised they were taking.
        </P>

        {/* ── 2. The big number ────────────────────────────────────────── */}
        <H2>Every ranking is secretly ten opinions</H2>
        <P>
          Here's the trick that turns a party game into a dataset. When you rank five cards, you're not
          just making a list — you're quietly casting <strong>ten separate votes</strong>. Putting Quality
          Time above Gift Giving is one vote. Physical Touch over Words of Affirmation is another. Every
          possible pair gets a verdict:
        </P>

        <Box sx={{ my: 3, p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "rgba(0,0,0,0.02)" }}>
          <Stack spacing={1.5}>
            <BinaryComparison winner="Quality Time" loser="Gift Giving" />
            <BinaryComparison winner="Physical Touch" loser="Words of Affirmation" />
            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center", display: "block" }}>
              …and 8 more pairs, all from this one ranking. Five cards = ten comparisons (that's "5 choose 2").
            </Typography>
          </Stack>
        </Box>

        <P>
          We've now recorded <Hl>112,407</Hl> of these honest rankings. Multiply by ten, and you get the
          number in the title:
        </P>

        <BigNumber
          value="1,124,070"
          before="head-to-head matchups, each one a person saying"
          after={<em>"I'd rather have this than that."</em>}
        />

        <P>
          Those comparisons came out of <Hl>15,833 games</Hl> played by <Hl>31,454 people</Hl>. Everything
          below is built from that pile. We'll start simple — what the whole crowd agrees on — and work our
          way up to the genuinely strange stuff.
        </P>

        {/* ── 3. ELO ───────────────────────────────────────────────────── */}
        <H2>Turning a million votes into a leaderboard</H2>
        <P>
          Once you have a giant heap of "A beats B" verdicts, an obvious question appears: across
          <em> everyone</em>, which answers win the most? This is the exact problem chess solved decades
          ago — lots of one-on-one games, and you want a single number that says how strong each player is.
          So we borrowed chess's answer: <Hl>the ELO rating</Hl>.
        </P>
        <P>
          Every answer card starts at 1500. Win a matchup and it gains a few points; lose and it drops a
          few. Run that across all 1.1 million comparisons and each card settles into a rating that captures
          how the whole community feels about it. Here's the single most-answered question in the entire
          game — love languages, ranked by <strong>3,528 people</strong>:
        </P>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", my: 3 }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>What is your love language?</Typography>
            <Typography variant="caption" color="text.secondary">3,528 players · 35,280 comparisons</Typography>
          </Box>
          <Box sx={{ px: 0.5 }}>
            {LOVE_LANGUAGES.map((c, i) => <CardRow key={i} card={c} rank={i} totalMatches={c.wins + c.losses} />)}
          </Box>
        </Paper>

        <P>
          <Hl>Quality Time runs away with it</Hl> at 1643, with Physical Touch close behind. And then there's
          Gift Giving, sitting alone at the bottom on 1287 — a <strong>356-point gap</strong> from the leader.
          Look at its bar in the next chart and you'll see why: nearly half of everyone who played put it
          dead last.
        </P>

        {/* ── 4. Distribution bars ─────────────────────────────────────── */}
        <H2>How to read the colourful bars</H2>
        <P>
          You'll see those little striped bars everywhere on this page, so here's the key. Each one shows
          <em> what fraction of people put a card in each position</em>:{" "}
          <Hl color="#bfa100">yellow = ranked #1</Hl>,{" "}
          <Hl color="#5B8DEF">blue = #2</Hl>,{" "}
          <Hl color="#EC5555">red = #3</Hl>,{" "}
          <Hl color="#4CAF50">green = #4</Hl>,{" "}
          <Hl color="#FF9800">orange = last</Hl>. A bar that leans yellow-left is a crowd favourite; a bar
          drowning in orange is widely disliked; a flat, evenly-mixed bar means people just can't agree.
        </P>

        <Stack spacing={2} sx={{ my: 3 }}>
          {[
            { dist: [313, 279, 184, 76, 30], title: "Quality Time", note: "Leans hard to the yellow. Most people rank it #1 or #2 — a clear favourite." },
            { dist: [56, 78, 111, 195, 442], title: "Gift Giving", note: "Drowning in orange. Half of all players put it dead last." },
            { dist: [92, 158, 223, 228, 181], title: "Words of Affirmation", note: "Almost perfectly flat. Every position is equally likely — pure disagreement." },
          ].map((row, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
              <RankDistBar dist={row.dist} width={190} height={26} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" fontWeight="bold">{row.title}</Typography>
                <Typography variant="body2" color="text.secondary">{row.note}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        <P>
          That last one — the flat bar — is the most interesting thing in the dataset, and it has a name:
          <Hl> entropy</Hl>. High entropy means high disagreement. Keep an eye on it, because it's about to
          do something surprising.
        </P>

        {/* ── 5. Consensus ─────────────────────────────────────────────── */}
        <H2>What the whole world agrees on</H2>
        <P>
          Some questions produce eerily consistent answers — everyone, regardless of who they're playing
          with, ranks them the same way. Ask <em>"what's the best way for a friend to wake you up?"</em> and
          the verdict is nearly unanimous:
        </P>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", my: 3 }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>Best way to be woken up by a friend</Typography>
            <Chip size="small" label="strong consensus" sx={{ bgcolor: "#4CAF50", color: "#fff", fontWeight: 700, fontSize: "0.6rem" }} />
          </Box>
          <Box sx={{ px: 0.5 }}>{WOKEN_UP.map((c, i) => <CardRow key={i} card={c} rank={i} />)}</Box>
        </Paper>

        <P>
          Just let them sleep. Failing that, a gentle pat. And absolutely, under no circumstances, a bucket
          of ice water — which 80% of people put in the bottom two. The bars are sharp spikes, not flat
          smears: this is what agreement looks like.
        </P>
        <P>
          Moral questions are even starker. Ask <em>"what would make you most angry at a friend?"</em> and
          one answer towers over everything:
        </P>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ my: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#EC5555", letterSpacing: "0.06em",
              textTransform: "uppercase", display: "block", mb: 1 }}>Unforgivable</Typography>
            {ANGRY_FRIEND_TOP.map((c, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <RankDistBar dist={c.dist} width={64} height={10} />
                <Typography variant="caption" sx={{ flex: 1 }}>{c.answer}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#4CAF50", letterSpacing: "0.06em",
              textTransform: "uppercase", display: "block", mb: 1 }}>Forgiven instantly</Typography>
            {ANGRY_FRIEND_BOTTOM.map((c, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <RankDistBar dist={c.dist} width={64} height={10} />
                <Typography variant="caption" sx={{ flex: 1 }}>{c.answer}</Typography>
              </Box>
            ))}
          </Box>
        </Stack>

        <P>
          "They slept with your partner" scores <Hl>1997 out of a possible ~2000</Hl> — about as close to a
          unanimous human verdict as this dataset ever produces. Three quarters of everyone ranked it the
          single most enraging thing a friend could do. At the other end, liking pineapple on pizza is, it
          turns out, entirely forgivable.
        </P>

        {/* ── 6. Chaos ─────────────────────────────────────────────────── */}
        <H2>What nobody can agree on</H2>
        <P>
          Now the opposite. Some questions produce almost perfectly flat bars — every answer lands at every
          rank about equally often, and the ELO scores barely move from the starting 1500. The champion of
          chaos is this question about romantic movie tropes, where all five answers finish within a laughable
          <Hl> 30 ELO points</Hl> of each other:
        </P>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", my: 3 }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>Rank these movie tropes, hottest to least</Typography>
            <Chip size="small" label="total chaos" sx={{ bgcolor: "#FF9800", color: "#fff", fontWeight: 700, fontSize: "0.6rem" }} />
          </Box>
          <Box sx={{ px: 0.5 }}>{MOVIE_TROPES.map((c, i) => <CardRow key={i} card={c} rank={i} />)}</Box>
        </Paper>

        <P>
          For scale: love languages had a 356-point spread. This one has 30. The bars are near-uniform
          rainbows. On a question like this, knowing the global consensus tells you <em>nothing</em> —
          because there isn't one. Which sets up the most surprising finding on this page.
        </P>

        {/* ── 7. Prediction paradox ────────────────────────────────────── */}
        <H2>And yet — your friends still nail it</H2>
        <P lead>
          You'd think a question nobody agrees on would be impossible to predict. You'd be wrong.
        </P>
        <P>
          Remember, the game isn't about guessing the <em>crowd</em> — it's about guessing the specific
          person in front of you. A random guess scores about <strong>1,100 points</strong> out of 2,000.
          So that's our baseline: beat 1,100 and real knowledge is happening.
        </P>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ my: 3 }}>
          <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#4CAF50", display: "block", mb: 0.5 }}>
              Everyone agrees
            </Typography>
            <Typography variant="body2" fontWeight="bold">Best way to be woken up</Typography>
            <Box sx={{ my: 1.5 }}>
              <Typography sx={{ fontFamily: '"Bangers", cursive', fontSize: "2.2rem", color: "#4CAF50", lineHeight: 1 }}>1,577</Typography>
              <Typography variant="caption" color="text.secondary">avg points · easy to call</Typography>
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#FF9800", display: "block", mb: 0.5 }}>
              Nobody agrees
            </Typography>
            <Typography variant="body2" fontWeight="bold">Hottest movie trope</Typography>
            <Box sx={{ my: 1.5 }}>
              <Typography sx={{ fontFamily: '"Bangers", cursive', fontSize: "2.2rem", color: "#FF9800", lineHeight: 1 }}>1,444</Typography>
              <Typography variant="caption" color="text.secondary">avg points · still way above chance</Typography>
            </Box>
          </Paper>
        </Stack>

        <P>
          The movie-tropes question has essentially zero global consensus — and friends <em>still</em> score
          1,444, a full 30% better than a coin flip. The conclusion is kind of lovely: you don't need humanity
          to agree on something for your friends to know <em>your</em> answer. They've been paying attention.
          Your group knows you have opinions about the "only one bed" situation, even if the rest of the planet
          is split down the middle.
        </P>

        {/* ── 8. Relationship values ───────────────────────────────────── */}
        <H2>The big one: what matters in a relationship</H2>
        <P>
          One question pulls all of this together. It has 17 answer cards and was ranked by hundreds of
          people, and it splits cleanly into a top half everyone agrees on and a bottom half that descends
          into beautiful chaos.
        </P>

        <Box sx={{ overflowX: "auto", my: 3 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", minWidth: 340 }}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>What's most important in a relationship?</Typography>
              <Typography variant="caption" color="text.secondary">272 players</Typography>
            </Box>
            <Box sx={{ px: 0.5 }}>{RELATIONSHIP_IMPORTANCE.map((c, i) => <CardRow key={i} card={c} rank={i} />)}</Box>
          </Paper>
        </Box>

        <P>
          <Hl>Loyalty wins</Hl>, decisively, at 1815 — followed by Honesty, Effort, Communication and Trust,
          all clustered tightly together with similar bars. People broadly agree on what a good relationship
          needs. Then it falls apart: "Money" ranks second-to-last, beaten only by "Butt stuff," which 60% of
          players dropped to the very bottom — even as a stubborn 8% planted it at #1. Those are the two
          camps, and they are not going to reconcile.
        </P>

        {/* ── More questions tray ──────────────────────────────────────── */}
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 0.5 }}>A few more, if you're curious</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Scroll sideways. Each card is a question, its top five answers, and how well friends predict it.</Typography>
        <CardTray>
          <QuestionCard question="What would make you most angry at a friend?" cards={[...ANGRY_FRIEND_TOP, ...ANGRY_FRIEND_BOTTOM]} predScore={1537} tag="consensus" tagColor="#4CAF50" />
          <QuestionCard question="What's most important in a relationship?" cards={RELATIONSHIP_IMPORTANCE.slice(0, 5)} predScore={1453} tag="most played" tagColor="#5B8DEF" />
          <QuestionCard question="What is your love language?" cards={LOVE_LANGUAGES} predScore={1486} tag="most played" tagColor="#5B8DEF" />
          <QuestionCard question="Best way to be woken up by a friend" cards={WOKEN_UP} predScore={1577} tag="predictable" tagColor="#9C27B0" />
          <QuestionCard question="Rank these movie tropes, hottest to least" cards={MOVIE_TROPES} predScore={1444} tag="chaos" tagColor="#FF9800" />
        </CardTray>

        {/* ── What a miss looks like ───────────────────────────────────── */}
        <H2>What a wrong guess looks like</H2>
        <P>
          When you predict someone's ranking, the game scores you on how far each card is from where it
          should be — and it punishes big misses harder than small ones. Here's a guess laid against the
          truth. Green lines stayed put (full points); a coloured line that crosses the gap is a card you
          moved, and the further it travelled, the more it cost:
        </P>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0, my: 3 }}>
          <Box sx={{ width: "38%", flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 0.75, fontWeight: 600 }}>
              Their real answer
            </Typography>
            <MiniRankList items={czarOrder} />
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: 5 * 34 - 4 + 20 }}>
            <Box sx={{ position: "absolute", top: 20, left: 0, right: 0, bottom: 0 }}>
              <SequenceConnector leftOrder={czarOrder} rightOrder={yourOrder} />
            </Box>
          </Box>
          <Box sx={{ width: "38%", flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 0.75, fontWeight: 600 }}>
              Your guess
            </Typography>
            <MiniRankList items={yourOrder} />
          </Box>
        </Box>

        {/* ── Spurious correlations ────────────────────────────────────── */}
        <H2>The weird part: questions that secretly predict each other</H2>
        <P>
          Now for the fun. Plenty of people have, over the years, answered <em>several</em> different
          questions honestly. That lets us ask a question the game was never designed for: does how you answer
          one thing predict how you answer something <em>completely unrelated?</em>
        </P>
        <P>
          For every pair of popular questions, we found the people who answered both and measured whether
          their rankings moved together. Most pairs show nothing. A few show something. And a couple show
          something we have absolutely no way to explain:
        </P>

        <Stack spacing={2} sx={{ my: 3 }}>
          {CORRELATIONS.map((c, i) => <CorrelationCard key={i} corr={c} />)}
        </Stack>

        <P>
          A quick word of honesty: these are drawn from 40–70 shared players each. That's enough to raise an
          eyebrow, not enough to publish a paper. Cooking-not-clubbing makes intuitive sense. The eyebrow
          thing almost certainly does not. We're showing it to you anyway, because that's the whole appeal of
          a pile of data this big — sometimes it just hands you a mystery.
        </P>

        {/* ── Methodology ──────────────────────────────────────────────── */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: "rgba(0,0,0,0.02)", mt: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>The fine print</Typography>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              <strong>ELO:</strong> every card starts at 1500. Each five-card ranking becomes ten head-to-head
              matchups, and we apply the standard chess ELO update after each one, accumulated across every game ever played.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Entropy:</strong> Shannon entropy over how often a card lands at each rank, scaled to 0–1.
              0 means everyone agrees; 1 means total disagreement (that's the flat bar).
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Prediction score:</strong> average points a guesser earns on a question. 2000 is a perfect
              read, ~1100 is a random guess, 0 is the exact reverse.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>What we left out:</strong> questions that fill in your friends' names are excluded — they're
              specific to each group and don't mean anything across the whole player base. Skipped turns are dropped too.
            </Typography>
          </Stack>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ fontSize: "1.15rem", mb: 2.5, fontWeight: 500 }}>
            Want to add your own honest opinions to the pile?
          </Typography>
          <Button variant="contained" size="large" onClick={() => history.push("/play")} sx={{ borderRadius: 3, px: 5, py: 1.25, fontSize: "1.05rem" }}>
            Play rank.io
          </Button>
        </Box>

      </Container>
    </Box>
  );
}
